import {
  Component,
  Injector,
  OnInit,
  forwardRef,
  inject,
  input,
  output,
  signal,
  computed,
  ChangeDetectionStrategy,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';

export interface SoRadioOption<T = any> {
  label: string;
  value: T;
  description?: string;
  icon?: string;
  disabled?: boolean;
}

let nextUniqueId = 0;

@Component({
  selector: 'so-radio-buttons-ui',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './so-radio-buttons.component.html',
  styleUrl: './so-radio-buttons.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SoRadioButtonsUI),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SoRadioButtonsUI<T = any> implements ControlValueAccessor, OnInit {
  // Inputs
  options = input<ReadonlyArray<SoRadioOption<T>>>([]);
  name = input<string>('');
  label = input<string>('');
  direction = input<'horizontal' | 'vertical'>('horizontal');
  columns = input<number>(0);
  size = input<'sm' | 'md' | 'lg'>('md');
  variant = input<'default' | 'card' | 'button'>('default');
  disabled = input<boolean>(false);
  required = input<boolean>(false);
  value = input<T | undefined>(undefined);

  // Outputs
  valueChange = output<T>();

  // Internal state
  protected internalValue = signal<T | null>(null);
  protected isDisabled = signal<boolean>(false);
  protected readonly generatedName = `so-radio-buttons-${nextUniqueId++}`;

  // Computed
  private injector = inject(Injector);
  private controlRequired = signal(false);
  protected effectiveRequired = computed(() => this.required() || this.controlRequired());
  protected groupName = computed(() => this.name() || this.generatedName);
  
  protected normalizedColumns = computed(() => {
    const cols = this.columns();
    return Math.max(0, Math.floor(cols));
  });

  protected gridTemplateColumns = computed(() => {
    const cols = Math.max(1, this.normalizedColumns());
    return `repeat(${cols}, minmax(0, 1fr))`;
  });

  protected layoutClass = computed(() => {
    const cols = this.normalizedColumns();
    if (cols > 1) return 'so-radio-buttons--grid';
    return this.direction() === 'vertical' 
      ? 'so-radio-buttons--vertical' 
      : 'so-radio-buttons--horizontal';
  });

  ngOnInit(): void {
    const ngControl = this.injector.get(NgControl, null);
    queueMicrotask(() => {
      const control = ngControl?.control;
      if (control?.hasValidator) {
        this.controlRequired.set(control.hasValidator(Validators.required));
      }
    });
  }

  constructor() {
    // Sync value input with internal state
    effect(() => {
      const inputValue = this.value();
      if (inputValue !== undefined) {
        this.internalValue.set(inputValue);
      }
    }, {  });

    // Sync disabled input
    effect(() => {
      this.isDisabled.set(this.disabled());
    }, {  });
  }

  // ControlValueAccessor
  private onChange: (value: T) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: T | null): void {
    this.internalValue.set(value);
  }

  registerOnChange(fn: (value: T) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  // Methods
  isChecked(optionValue: T): boolean {
    return this.internalValue() === optionValue;
  }

  selectOption(option: SoRadioOption<T>): void {
    if (this.isDisabled() || option.disabled) return;
    
    this.internalValue.set(option.value);
    this.onChange(option.value);
    this.onTouched();
    this.valueChange.emit(option.value);
  }

  handleBlur(): void {
    this.onTouched();
  }

  trackByValue(_index: number, option: SoRadioOption<T>): any {
    return option.value;
  }
}
